import { colorMap } from '@/product-colors'

export const FILTER_PARAM_MAP = {
  Brand: 'brand_ids',
  Gender: 'gender',
  Category: 'categories',
  Subcategory: 'sub_categories',
  colors: 'colors',
  color_family_ids: 'color_family_ids',
}

export function createFilterOptions(brands, products) {
  const createOptions = (items, key) => {
    const map = new Map()
    items.forEach(item => {
      const val = item[key]
      if (val) map.set(val, { label: val, value: val })
    })
    return Array.from(map.values())
  }

  return [
    {
      name: 'Brand',
      options: brands.map(b => ({ label: b.name, value: b.id })),
    },
    {
      name: 'Gender',
      options: createOptions(products, 'gender'),
    },
    {
      name: 'Category',
      options: createOptions(products, 'category'),
    },
    {
      name: 'Subcategory',
      options: createOptions(products, 'sub_category'),
    },
  ]
}

// Filter component utility functions

/**
 * Selection state utilities
 */

/**
 * Check if a filter option is selected
 * @param {string|number} optionValue - The value of the option to check
 * @param {Array} selectedValues - The array of selected values
 * @returns {boolean} - Whether the option is selected
 */
export const isValueSelected = (optionValue, selectedValues = []) => Array.isArray(selectedValues) && selectedValues.includes(optionValue)

/**
 * Check if a color is selected in the filter
 * @param {string} colorValue - The color value to check
 * @param {Object} selectedFilters - The selected filter object
 * @returns {boolean} - Whether the color is selected
 */
export const isColorSelected = (colorValue, selectedFilters = {}) => Boolean(selectedFilters.colors && selectedFilters.colors.includes(colorValue))

/**
 * Check if a color family group is checked
 * @param {Object} group - The color family group
 * @param {Object} selectedValues - The selected values object
 * @returns {boolean} - Whether the group is checked
 */
export const isGroupChecked = (group, selectedValues = {}) => Boolean(
  selectedValues.color_family_ids
  && selectedValues.color_family_ids.includes(group.id),
)

/**
 * Check if a color family group is in indeterminate state
 * @param {Object} group - The color family group
 * @param {Object} selectedValues - The selected values object
 * @returns {boolean} - Whether the group is in indeterminate state
 */
export const isGroupIndeterminate = (group, selectedValues = {}) => {
  const selected = selectedValues.colors || []
  const hasSome = group.colors && group.colors.some(c => selected.includes(c.value))
  const isChecked = isGroupChecked(group, selectedValues)
  return Boolean(hasSome && !isChecked)
}

/**
 * Filter merging utilities
 */

/**
 * Generic function to merge filter collections, ensuring uniqueness by a key
 * @param {Array} existing - Existing items array
 * @param {Array} incoming - New items to merge
 * @param {Object} options - Configuration options
 * @param {string|Function} options.identifierKey - Property or function to use as unique identifier
 * @param {Function} options.mergeItems - Custom function to merge two items
 * @param {Function} options.sortItems - Function to sort items after merging
 * @param {Function} options.transformItem - Function to transform an item before adding it
 * @returns {Array} - Merged collection of items
 */
export const mergeFilterCollections = (existing = [], incoming = [], options = {}) => {
  // Default options
  const {
    identifierKey = 'id',
    mergeItems = (existingItem, incomingItem) => ({ ...existingItem, ...incomingItem }),
    sortItems = null,
    transformItem = item => item,
  } = options

  // Helper for getting the identifier value
  const getIdentifier = item => {
    if (typeof identifierKey === 'function') {
      return identifierKey(item)
    }
    return item[identifierKey]
  }

  // Create a copy to avoid mutating the original
  const result = [...existing]

  // Map existing items by identifier for quick lookup
  const existingMap = new Map(result.map(item => [getIdentifier(item), item]))

  // Process incoming items
  for (const incomingItem of incoming) {
    const transformedItem = transformItem(incomingItem)
    const itemId = getIdentifier(transformedItem)

    if (existingMap.has(itemId)) {
      // Merge with existing item
      const existingItem = existingMap.get(itemId)
      const mergedItem = mergeItems(existingItem, transformedItem)

      // Replace existing item with merged item
      const index = result.findIndex(item => getIdentifier(item) === itemId)
      result[index] = mergedItem
      existingMap.set(itemId, mergedItem)
    } else {
      // Add new item
      result.push(transformedItem)
      existingMap.set(itemId, transformedItem)
    }
  }

  // Sort if needed
  if (sortItems) {
    return result.sort(sortItems)
  }

  return result
}

/**
 * Merge filter options (simple name/value pairs)
 * @param {Array} existingOptions - Existing filter options
 * @param {Array} incomingOptions - New filter options to merge
 * @returns {Array} - Merged filter options with unique values
 */
export const mergeFilterOptions = (existingOptions = [], incomingOptions = []) => mergeFilterCollections(existingOptions, incomingOptions, {
  identifierKey: 'name',
  mergeItems: (existing, incoming) => {
    const existingValues = new Set(existing.options.map(o => o.value))
    const uniqueNewOptions = incoming.options.filter(o => !existingValues.has(o.value))

    return {
      ...existing,
      options: [...existing.options, ...uniqueNewOptions],
    }
  },
})

/**
 * Color family utilities
 */

/**
 * Build color families from products
 * @param {Array} products - Array of product data
 * @param {Set} expandedColorFamilies - Set of expanded color families
 * @returns {Array} - Array of color family objects
 */
export const buildColorFamilies = (products, expandedColorFamilies = new Set()) => {
  const map = new Map()

  for (const product of products) {
    const items = Array.isArray(product.items) ? product.items : []

    for (const item of items) {
      tryAddColorToFamily(item, map, expandedColorFamilies)
    }
  }

  return finalizeColorFamilies(map)
}

/**
 * Try to add a color to its appropriate family
 * @param {Object} item - The product item
 * @param {Map} map - Map of color families
 * @param {Set} expandedColorFamilies - Set of expanded color families
 */
export const tryAddColorToFamily = (item, map, expandedColorFamilies) => {
  const family = getOrCreateColorFamily(map, item, expandedColorFamilies)
  const color = (item.color || '').trim()

  if (!shouldAddColor(family, color)) {
    return
  }

  const colorLower = color.toLowerCase()
  const alreadyExists = family.colors.some(c => c.name.toLowerCase() === colorLower)

  if (!alreadyExists) {
    family.colors.push({ name: color, value: color })
  }
}

/**
 * Check if a color should be added to a family
 * @param {Object} family - The color family
 * @param {string} color - The color name
 * @returns {boolean} - Whether the color should be added
 */
export const shouldAddColor = (family, color) => Boolean(
  family
  && color
  && color.toLowerCase() !== family.name.toLowerCase(),
)

/**
 * Sort and finalize color families
 * @param {Map} map - Map of color families
 * @returns {Array} - Sorted array of color families
 */
export const finalizeColorFamilies = map => {
  const families = Array.from(map.values())
  families.forEach(f => f.colors.sort((a, b) => a.name.localeCompare(b.name)))
  families.sort((a, b) => a.name.localeCompare(b.name))
  return families
}

/**
 * Get or create a color family in the map
 * @param {Map} map - Map of color families
 * @param {Object} item - Product item with color information
 * @param {Set} expandedColorFamilies - Set of expanded color families
 * @returns {Object|null} - The color family object or null
 */
export const getOrCreateColorFamily = (map, item, expandedColorFamilies = new Set()) => {
  const id = item.color_family_id
  const colorInfo = id != null ? colorMap.get(id) : null
  if (id == null || !colorInfo) return null

  if (!map.has(id)) {
    map.set(id, {
      id,
      name: colorInfo.name,
      value: colorInfo.name,
      colorCode: colorInfo.code,
      expanded: expandedColorFamilies.has(colorInfo.name),
      colors: [],
    })
  }
  return map.get(id)
}

/**
 * Merge new color families with existing ones
 * @param {Array} existingFamilies - Existing color families array
 * @param {Array} newProducts - New products to extract families from
 * @param {Set} expandedColorFamilies - Set of expanded color families
 * @returns {Array} - Updated color families array
 */
export const mergeColorFamilies = (existingFamilies, newProducts, expandedColorFamilies = new Set()) => {
  const newFamilies = buildColorFamilies(newProducts, expandedColorFamilies)

  return mergeFilterCollections(existingFamilies, newFamilies, {
    identifierKey: 'id',
    mergeItems: (existing, incoming) => {
      // Create set of existing color values (lowercase) for deduplication
      const existingValues = new Set(
        existing.colors.map(c => c.value.toLowerCase()),
      )

      // Filter out duplicate colors and merge
      const newColors = incoming.colors.filter(
        c => !existingValues.has(c.value.toLowerCase()),
      )

      const mergedColors = [...existing.colors, ...newColors]
      mergedColors.sort((a, b) => a.name.localeCompare(b.name))

      return {
        ...existing,
        colors: mergedColors,
      }
    },
    sortItems: (a, b) => a.name.localeCompare(b.name),
    transformItem: item => ({
      ...item,
      expanded: item.expanded || (existingFamilies.find(f => f.id === item.id)?.expanded || false),
    }),
  })
}

/**
 * Format utilities
 */

/**
 * Format a price value
 * @param {number} value - The price value
 * @returns {string} - Formatted price string
 */
export const formatPrice = value => `$${Math.round(value)}`

/**
 * Format minimum price with special handling for min value
 * @param {number} value - The min price value
 * @param {number} min - The absolute minimum
 * @param {boolean} noMinimum - Whether to show "0" for minimum values
 * @returns {string} - Formatted min price string
 */
export const formatMinPrice = (value, min, noMinimum = true) => (value <= min && noMinimum ? '$0' : formatPrice(value))

/**
 * Format maximum price with special handling for max value
 * @param {number} value - The max price value
 * @param {number} max - The absolute maximum
 * @param {boolean} noMaximum - Whether to show "max+" for maximum values
 * @returns {string} - Formatted max price string
 */
export const formatMaxPrice = (value, max, noMaximum = true) => (value >= max && noMaximum ? `$${Math.round(max)}+` : formatPrice(value))

/**
 * UI utilities
 */

/**
 * Get the icon name based on expanded state
 * @param {Object} group - The group with expanded state
 * @returns {string} - The icon name
 */
export const getExpandIconName = group => (group.expanded ? 'ChevronUpIcon' : 'ChevronDownIcon')
